import type { Rng } from '../../core/rng';
import type { Vec2 } from '../../geo/types';
import { SHIP_TEMPLATES, type ShipType } from './shipTemplates';
import type { PlacedShip } from './shipPlacement';

/** Where an anchored vessel lies: out on the IJ beyond the outer pile row, or inside it. */
export type HarbourZone = 'roadstead' | 'innerHarbour';

/** The end of a ship that fires a salute. */
export type GunSmokeEnd = 'bow' | 'stern';

/** A ship has two or more masts, small craft have one and the rest none. */
export type Craft = 'ship' | 'smallCraft' | 'unmasted';

/** The part of the painter frame that shows an anchorage's water. */
export type FrameRegion = 'band' | 'aboveBand' | 'beyondFrame';

/** Vessels painted on a stretch of water, moored or at anchor, by craft. */
export interface PaintedTally {
  readonly craft: Readonly<Record<Craft, number>>;
  /** Those with at least one sail spread. */
  readonly underSail: Readonly<Record<Craft, number>>;
}

const CRAFTS: readonly Craft[] = ['ship', 'smallCraft', 'unmasted'];

/* The layout splits an anchorage at the painter frame (tools/layout/harbour.py): every part keeps
   the anchorage's id as its stem, then names the part of the frame and numbers split pieces
   ('anchorage-roadstead-2', 'anchorage-roadstead-above-band', 'anchorage-roadstead-beyond-frame'). */
const ZONE_OF_ANCHORAGE: Readonly<Record<string, HarbourZone>> = {
  'anchorage-roadstead': 'roadstead',
  'anchorage-inner-harbour': 'innerHarbour',
};
const REGION_OF_SUFFIX: Readonly<Record<string, FrameRegion>> = {
  '': 'band',
  '-above-band': 'aboveBand',
  '-beyond-frame': 'beyondFrame',
};
const PART_ID = new RegExp(
  `^(${Object.keys(ZONE_OF_ANCHORAGE).join('|')})(${Object.keys(REGION_OF_SUFFIX)
    .filter(Boolean)
    .join('|')})?(-\\d+)?$`
);

const NONE: Readonly<Record<Craft, number>> = { ship: 0, smallCraft: 0, unmasted: 0 };

/**
 * Counted vessel by vessel on the museum scan: docs/reference/ship-measure/harbour-count.json lists
 * every vessel and harbour-count.jpg marks it. The band is frame rows 700-1000 of the painter
 * frame; above it the painting shows only 3 ships with bare masts outside the outer pile row and 4
 * boats without a mast inside it.
 */
export const PAINTED: Readonly<
  Record<HarbourZone, Readonly<Record<'band' | 'aboveBand', PaintedTally>>>
> = {
  roadstead: {
    band: {
      craft: { ship: 24, smallCraft: 11, unmasted: 0 },
      underSail: { ship: 8, smallCraft: 7, unmasted: 0 },
    },
    aboveBand: { craft: { ship: 3, smallCraft: 0, unmasted: 0 }, underSail: NONE },
  },
  innerHarbour: {
    band: { craft: { ship: 11, smallCraft: 17, unmasted: 8 }, underSail: NONE },
    aboveBand: { craft: { ship: 0, smallCraft: 0, unmasted: 4 }, underSail: NONE },
  },
};

function added(
  a: Readonly<Record<Craft, number>>,
  b: Readonly<Record<Craft, number>>
): Record<Craft, number> {
  return {
    ship: a.ship + b.ship,
    smallCraft: a.smallCraft + b.smallCraft,
    unmasted: a.unmasted + b.unmasted,
  };
}

/** The painted vessels a part of an anchorage follows; beyond the frame, those of the whole frame. */
export function paintedTally(zone: HarbourZone, region: FrameRegion): PaintedTally {
  const { band, aboveBand } = PAINTED[zone];
  if (region !== 'beyondFrame') return region === 'band' ? band : aboveBand;
  return {
    craft: added(band.craft, aboveBand.craft),
    underSail: added(band.underSail, aboveBand.underSail),
  };
}

/**
 * 5 of the 8 roadstead ships under sail fire a salute; no furled ship, no boat and nothing inside
 * the pile row smokes.
 */
export const SALUTING_SHARE = 5 / 8;

/** The ends the painting's saluting ships fire from, west to east: 4 bows and 2 sterns in 6 puffs. */
export const PAINTED_SALUTES: readonly (readonly GunSmokeEnd[])[] = [
  ['bow'],
  ['bow', 'stern'],
  ['bow'],
  ['stern'],
  ['bow'],
];

function partOf(anchorageId: string): { readonly zone: HarbourZone; readonly region: FrameRegion } {
  const match = PART_ID.exec(anchorageId);
  if (!match) throw new Error(`Anchorage ${anchorageId} lies in no known harbour zone`);
  return { zone: ZONE_OF_ANCHORAGE[match[1]!]!, region: REGION_OF_SUFFIX[match[2] ?? '']! };
}

export function harbourZoneOf(anchorageId: string): HarbourZone {
  return partOf(anchorageId).zone;
}

export function frameRegionOf(anchorageId: string): FrameRegion {
  return partOf(anchorageId).region;
}

export function craftOf(type: ShipType): Craft {
  const masts = SHIP_TEMPLATES[type].masts.length;
  if (masts === 0) return 'unmasted';
  return masts === 1 ? 'smallCraft' : 'ship';
}

/** Whole numbers of each craft for `count` vessels of a part of a zone, in the painted proportion. */
export function craftQuota(
  zone: HarbourZone,
  region: FrameRegion,
  count: number
): Readonly<Record<Craft, number>> {
  const painted = paintedTally(zone, region).craft;
  const total = CRAFTS.reduce((sum, craft) => sum + painted[craft], 0);
  const exact = CRAFTS.map((craft) => (count * painted[craft]) / total);
  const floors = exact.map(Math.floor);
  const byRemainder = CRAFTS.map((_, i) => i).sort(
    (a, b) => exact[b]! - floors[b]! - (exact[a]! - floors[a]!)
  );
  const extra = new Set(byRemainder.slice(0, count - floors.reduce((a, b) => a + b, 0)));
  return Object.fromEntries(
    CRAFTS.map((craft, i) => [craft, floors[i]! + (extra.has(i) ? 1 : 0)])
  ) as Record<Craft, number>;
}

export function sailSetShare(zone: HarbourZone, region: FrameRegion, type: ShipType): number {
  const craft = craftOf(type);
  const { craft: painted, underSail } = paintedTally(zone, region);
  return painted[craft] > 0 ? underSail[craft] / painted[craft] : 0;
}

/**
 * Spreads sail on the painted share of each craft among an anchorage's vessels, chosen by the
 * rng, so the anchorage shows the counted number of sails rather than a chance draw of it.
 */
export function withSails(
  ships: readonly PlacedShip[],
  zone: HarbourZone,
  region: FrameRegion,
  rng: Rng
): readonly PlacedShip[] {
  const spread = new Set(
    CRAFTS.flatMap((craft) => {
      const ofCraft = ships.filter((ship) => craftOf(ship.type) === craft);
      const share = ofCraft.length > 0 ? sailSetShare(zone, region, ofCraft[0]!.type) : 0;
      return rng
        .shuffle(ofCraft)
        .slice(0, Math.round(ofCraft.length * share))
        .map((ship) => ship.id);
    })
  );
  return ships.map((ship) => ({ ...ship, sailState: spread.has(ship.id) ? 'set' : 'furled' }));
}

/**
 * Hands the painting's salutes to the painted share of an anchorage's ships under sail, drawn by
 * the rng, so a small fleet shows the painted proportion rather than a chance draw of it. Given
 * `away`, each ship fires once, from the end it names.
 */
export function withSalutes(
  ships: readonly PlacedShip[],
  zone: HarbourZone,
  rng: Rng,
  away?: (ship: PlacedShip) => GunSmokeEnd
): readonly PlacedShip[] {
  const underSail = ships.filter(
    (s) => zone === 'roadstead' && s.sailState === 'set' && SHIP_TEMPLATES[s.type].masts.length > 1
  );
  const firing = rng.shuffle(underSail).slice(0, Math.round(underSail.length * SALUTING_SHARE));
  const ends = new Map(
    firing.map((ship, i) => [
      ship.id,
      away ? [away(ship)] : PAINTED_SALUTES[i % PAINTED_SALUTES.length]!,
    ])
  );
  return ships.map((ship) => ({ ...ship, gunSmoke: ends.get(ship.id) ?? [] }));
}

/**
 * The end of a ship that points further from the painter camera's line of sight over the ground,
 * from `eye` along `viewHeading` (layout angles; a ship's heading is where its stern points). The
 * painting shows six puffs; a ship beyond the frame that fires from this end drifts its cloud away
 * along its own axis, out of the frame.
 */
export function endAwayFromView(
  ship: Pick<PlacedShip, 'position' | 'heading'>,
  eye: Vec2,
  viewHeading: number
): GunSmokeEnd {
  const left: Vec2 = [-Math.sin(viewHeading), Math.cos(viewHeading)];
  const side = (ship.position[0] - eye[0]) * left[0] + (ship.position[1] - eye[1]) * left[1];
  const stern = Math.cos(ship.heading) * left[0] + Math.sin(ship.heading) * left[1];
  return stern * side > 0 ? 'stern' : 'bow';
}
